<script lang="ts">
  import { createEventDispatcher, onDestroy, onMount } from "svelte";
  import FulfillmentMethodPicker from "@retailer/svelte/Shared/FulfillmentMethodPicker.svelte";
  import Sortable from "sortablejs";
  import Pagination from "@shared/svelte/Components/Pagination/Large.svelte";
  import ProductListRow from "@retailer/svelte/Shared/QuickOrderList/ProductListRow.svelte";
  import MobileProductListRow from "@retailer/svelte/Shared/QuickOrderList/MobileProductListRow.svelte";
  import ProductListGrid from "@retailer/svelte/Shared/QuickOrderList/ProductListGrid.svelte";
  import ProductListHeader from "@retailer/svelte/Shared/QuickOrderList/ProductListHeader.svelte";
  import ProductListTableHeader from "@retailer/svelte/Shared/QuickOrderList/ProductListTableHeader.svelte";
  import type { PaginationDetails } from "@shared/types/Pagination";
  import type { PageData } from "@shared/types/PageData";
  import type { QuickOrderListProduct } from "@shared/types/ProductLine";
  import type { QuickOrderListDetail, QuickOrderListFilters } from "@shared/types/QuickOrderList";
  import type { ListType } from "@shared/types/QuickOrderList";
  import type { Distributor } from "@shared/types/Distributor";
  import Flash from "@shared/services/flashv2";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import TrackingContext from "@shared/svelte/Components/Tracking/TrackingContext.svelte";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { compact } from "lodash";
  import RetailerUserAuthenticationsStore from "@retailer/svelte/stores/RetailerUserAuthenticationsStore";
  import { getControlStateAuthenticationService } from "@retailer/services/retailer_user_authentications_service";
  import { hideFulfillmentMethodPickerForDistributor } from "@retailer/services/fulfillment_methods_service";

  export let list: QuickOrderListDetail;
  export let filters: QuickOrderListFilters;
  export let page: PageData;
  export let products: QuickOrderListProduct[];
  export let editMode: boolean;
  export let readOnly: boolean = false;
  export let showInventory: boolean = false;
  export let sortObj = null;
  export let useListLayout: boolean;
  export let showLoading: boolean = false;
  export let fullCount: number = null;
  export let callbacks;
  export let listType: ListType = null;

  let currentPage: number = (page.params && parseInt(page.params.page, 10)) || 1;
  let filteredProductsCount: number = (products && products[0] && products[0].full_count) || 0;
  let fulfillmentMethodDistributor;
  let fulfillmentMethodSku: string | null;
  let totalProducts: number = fullCount || filteredProductsCount || 0;
  let showFulfillmentMethodsDrawer: boolean = false;
  let sortableList: HTMLElement | null = null;

  let pagination: PaginationDetails = {
    perPage: 50,
    page: currentPage,
    totalEntries: filteredProductsCount,
    goToPage: page => changePage(page),
  };

  const showPrices = !showInventory;
  $: {
    page;
    currentPage = (page.params && parseInt(page.params.page, 10)) || 1;
    filteredProductsCount = (products && products[0] && products[0].full_count) || 0;
    totalProducts = fullCount || filteredProductsCount || 0;
    pagination.page = (page.params && parseInt(page.params.page, 10)) || 1;
    pagination.totalEntries = filteredProductsCount;
  }

  $: forSculptureRetailer = list.for_sculpture_retailer;

  $: selectedProducts = [];

  $: if (showInventory || editMode) {
    selectedProducts = [];
  }
  let dispatch = createEventDispatcher();

  const changePage = async pageNumber => {
    document.body.scrollIntoView();
    page.params.page = pageNumber;
    dispatch("selectFilter", { filters: page.params });
  };

  const isFiltered = () => {
    return !Object.keys(page.params).every(key => {
      if (
        key !== "direction" &&
        key !== "page" &&
        key !== "listId" &&
        key !== "deleted" &&
        key !== "sort"
      ) {
        if (page.params[key]) return false;
      }
      return true;
    });
  };

  const toggleSelectAll = () => {
    if (selectedProducts.length === 0) {
      selectedProducts = products.map(product => product.id);
    } else {
      selectedProducts = [];
    }
  };

  const removeProducts = async () => {
    const productType = selectedProducts.length > 1 ? "Products" : "Product";

    HttpPromise({
      method: "DELETE",
      url: "/api/retailer/quick_order_lists/" + list.id + "/products/destroy_by_ids",
      data: {
        product_ids: selectedProducts,
      },
    }).then(
      function success(res) {
        page.params["deleted"] = selectedProducts[0];
        changePage(1);
        selectedProducts = [];
        Flash.now({ type: "success", message: "Success! " + productType + " removed from list." });
      },
      function error(res) {
        Flash.now({
          type: "error",
          header: "There was an error updating the list. Please try again.",
        });
      },
    );
  };

  function doSort() {
    const sortedIds = sortObj.toArray();
    if (!sortedIds || sortedIds.length === 0) return;
    page.sortOrder = sortedIds.map(id => ({ id }));
  }

  function initSortable() {
    if (sortObj || !sortableList || !useListLayout) return;
    sortObj = Sortable?.create(sortableList, {
      onSort: function () {
        doSort();
      },
      handle: ".drag-handle",
      chosenClass: "list-sortable-chosen",
      ghostClass: "list-sortable-ghost",
    });
  }

  function destroySortable() {
    if (!sortObj || !sortObj?.el) return;
    sortObj.destroy();
    sortObj = null;
  }

  onMount(() => {
    initSortable();
  });

  onDestroy(() => {
    destroySortable();
  });

  $: {
    if (useListLayout && sortableList) {
      initSortable();
    } else if (sortObj) {
      destroySortable();
    }
  }

  $: {
    if (sortObj) {
      if (showLoading) {
        sortObj.option("disabled", true);
      } else {
        sortObj.option("disabled", false);
      }
    }
  }

  let headerCheckboxChecked = false;
  const toggleSelectAllWrapper = function () {
    toggleSelectAll();
    headerCheckboxChecked = selectedProducts.length > 0;
  };

  $: {
    selectedProducts = selectedProducts;
    headerCheckboxChecked = selectedProducts.length > 0;
  }

  const removeProductsWrapper = function () {
    list.loading = true;
    return removeProducts();
  };

  const { trackMrcEvents, trackClick } = EventTracking;

  function openFulfillmentMethodsDrawer(
    e: CustomEvent<{
      distributor: Distributor;
      sku: string | null;
    }>,
  ): void {
    fulfillmentMethodDistributor = e.detail.distributor;
    fulfillmentMethodSku = e.detail.sku;
    showFulfillmentMethodsDrawer = true;
  }

  const viewTargetObject =
    products.length === 0 && isFiltered()
      ? { targetObjectType: "list_search_no_results_found" }
      : null;

  let authService = getControlStateAuthenticationService();

  let retailerAuthApplicable: boolean = false;
  function onShowOHLQMessage() {
    retailerAuthApplicable = true;
  }

  let authStatus: string;
  $: authStatus =
    retailerAuthApplicable &&
    authService?.getRetailerUserAuthenticationStatus?.($RetailerUserAuthenticationsStore);
  $: fulfillmentMethodProducts = products.map(product => product.product);
</script>

<style>
  @media (max-width: 560px) {
    .grid-custom {
      grid-template-columns: unset;
    }
  }
</style>

{#if !editMode}
  <ProductListHeader
    currentPage={page.current}
    {showInventory}
    visibleProducts={products.length}
    {totalProducts}
    {forSculptureRetailer}
    filterValues={filters}
    limitedOrdering={list.limited_ordering}
    {list}
    products={fulfillmentMethodProducts}
    filters={page.params}
    bind:useListLayout
    on:selectFilter
    on:openDrawer={openFulfillmentMethodsDrawer}
  />
{/if}

{#if retailerAuthApplicable && (authStatus == "pending" || authStatus == "invalid") && authService}
  {#if authStatus == "invalid" && authService?.ctaComponent}
    <div class="fizz-stack-32">
      <svelte:component this={authService?.ctaComponent} {authStatus} {authService} />
    </div>
  {:else if authService?.bannerComponent}
    <svelte:component this={authService?.bannerComponent} {authStatus} {authService} />
  {/if}
{/if}
<TrackingContext let:trackingContext>
  <div use:trackMrcEvents={compact([trackingContext.entities, viewTargetObject])}>
    {#if useListLayout}
      <div class="fizz-stack-16">
        {#if totalProducts && !readOnly && !showInventory}
          <div class="fizz-flex-row-8 fizz-stack-16 fizz-hide-on-large">
            {#if !editMode}
              <label class="fizz-flex-row-8" for="header-checkbox">
                {#key headerCheckboxChecked}
                  <input
                    type="checkbox"
                    name="checkbox"
                    data-testId="header-checkbox"
                    id="header-checkbox"
                    class="fizz-checkbox"
                    bind:checked={headerCheckboxChecked}
                    on:click|preventDefault={toggleSelectAllWrapper}
                  />
                {/key}
              </label>
              <div class="fizz-flex-row-24">Select all</div>
            {/if}
            {#if selectedProducts.length > 0}
              <div class="fizz-flex-row-24">
                {#if list.loading}
                  <strong class="fizz-no-wrap loading"> Loading... </strong>
                {:else}
                  <strong class="fizz-no-wrap" data-testId="number-selected-text">
                    {selectedProducts.length} selected
                  </strong>
                  <!-- svelte-ignore a11y-missing-attribute -->
                  <button
                    class="fizz-button-danger fizz-no-wrap fizz-button-small"
                    data-testId="delete-button"
                    on:click|preventDefault={() => removeProductsWrapper()}
                  >
                    <strong>Remove from list</strong>
                  </button>
                {/if}
              </div>
            {/if}
          </div>
        {/if}
        <table
          class="fizz-table fizz-text-medium fizz-expand-32-on-large fizz-expand-16-on-small fizz-table-align-top"
          class:fizz-row-select={!(readOnly || editMode || showInventory)}
        >
          <ProductListTableHeader
            {editMode}
            {page}
            {readOnly}
            {removeProducts}
            {list}
            {selectedProducts}
            {showInventory}
            {toggleSelectAll}
            {forSculptureRetailer}
            emptyList={!products.length}
            {showPrices}
            on:selectFilter
          />
          <tbody id="products-to-sort" bind:this={sortableList}>
            {#if products.length > 0}
              {#each products as product (product.id)}
                {#if $LayoutStore.isMobile && editMode}
                  <MobileProductListRow rowProduct={product} disabled={showLoading} />
                {:else}
                  <ProductListRow
                    rowProduct={product}
                    disabled={showLoading}
                    bind:editMode
                    {callbacks}
                    {readOnly}
                    {showInventory}
                    {forSculptureRetailer}
                    {showPrices}
                    {onShowOHLQMessage}
                    on:openDrawer={openFulfillmentMethodsDrawer}
                  >
                    <input
                      slot="selectable"
                      type="checkbox"
                      class="fizz-checkbox"
                      data-testId="row-checkbox"
                      bind:group={selectedProducts}
                      value={product.id}
                    />
                  </ProductListRow>
                {/if}
              {/each}
            {/if}
          </tbody>
        </table>
      </div>
    {:else}
      <div
        class="fizz-stack-16 fizz-table-align-top grid-custom"
        class:fizz-grid={$LayoutStore.isDesktop}
        style="--grid-col-max: 280px"
      >
        {#if products.length > 0}
          {#each products as product (product.id)}
            <ProductListGrid {callbacks} gridProduct={product} {showPrices} />
          {/each}
        {/if}
      </div>
    {/if}
  </div>

  {#if products.length == 0}
    {#if isFiltered()}
      <div class="fizz-text-center">
        <p class="fizz-stack-24 fizz-text-strong">No product results on this list</p>
        {#if listType === "user_created" && !list.limited_ordering}
          <p>
            Try modifying your search or
            <a
              href={"#"}
              on:click|preventDefault={event => {
                trackClick("list_add_product_from_no_results_cta", trackingContext.entities);
                callbacks.openListBuilder(event);
              }}>add a product to this list</a
            >
          </p>
        {:else}
          <p>Try modifying your search</p>
        {/if}
      </div>
    {:else}
      <div class="fizz-text-center">
        <p class="fizz-stack-24 fizz-text-strong">No products in this list</p>
        <p>You don’t have any products added to this list</p>
      </div>
    {/if}
  {/if}

  {#if !editMode && products.length > 0 && pagination.perPage < pagination.totalEntries}
    <Pagination {pagination} />
  {/if}
  {#if fulfillmentMethodDistributor && !hideFulfillmentMethodPickerForDistributor(fulfillmentMethodDistributor?.id)}
    <FulfillmentMethodPicker
      bind:open={showFulfillmentMethodsDrawer}
      distributor={fulfillmentMethodDistributor}
      products={fulfillmentMethodProducts}
      skus={fulfillmentMethodSku ? [fulfillmentMethodSku] : []}
      forProductLine={false}
    />
  {/if}
</TrackingContext>
